import React, { useState, useEffect, useMemo } from 'react';
import Layout from 'layout/Layout';
import { useGetAllSurveysQuery } from 'api/surveyApi';

const Survey = () => {
    const { data, isLoading, isError, refetch } = useGetAllSurveysQuery();
    const [searchTerm, setSearchTerm] = useState('');
    const [filterInterest, setFilterInterest] = useState('All');

    // Auto-refresh every 30 seconds
    useEffect(() => {
        const interval = setInterval(() => {
            refetch();
        }, 30000);

        return () => clearInterval(interval);
    }, [refetch]);

    const filteredSurveys = useMemo(() => {
        if (!data) return [];
        return data.filter((survey) => {
            const matchesSearch = 
                survey.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                survey.contact_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                survey.address?.toLowerCase().includes(searchTerm.toLowerCase());
            
            const matchesFilter = 
                filterInterest === 'All' ? true :
                filterInterest === 'Yes' ? survey.interested === 1 || survey.interested === true :
                survey.interested === 0 || survey.interested === false;

            return matchesSearch && matchesFilter;
        });
    }, [data, searchTerm, filterInterest]);

    return (
        <Layout title="Survey Responses" tagline="Manage and filter workshop participant surveys">
            <div className="max-w-7xl mx-auto mt-10 md:mt-16 px-4 sm:px-6 lg:px-8 mb-20">
                
                {/* Header & Controls Section */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 mb-6 flex flex-col md:flex-row justify-between items-center gap-4">
                    <div className="flex-1 w-full relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <input
                            type="text"
                            placeholder="Search by name, contact, or address..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-shadow"
                        />
                    </div>
                    
                    <div className="flex items-center gap-3 w-full md:w-auto">
                        <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Filter Interested:</label>
                        <select
                            value={filterInterest}
                            onChange={(e) => setFilterInterest(e.target.value)}
                            className="border border-gray-300 rounded-lg px-4 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[120px]"
                        >
                            <option value="All">All</option>
                            <option value="Yes">Yes</option>
                            <option value="No">No</option>
                        </select>
                    </div>
                </div>

                {/* Table Section */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    {isLoading && (
                        <div className="flex justify-center items-center h-40">
                            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-600"></div>
                        </div>
                    )}
                    
                    {isError && (
                        <div className="p-8 text-center text-red-500">
                            <p className="font-semibold">Error fetching surveys.</p>
                            <p className="text-sm">Please try refreshing the page.</p>
                        </div>
                    )}

                    {!isLoading && !isError && (
                        <div className="overflow-x-auto">
                            <table className="w-full whitespace-nowrap text-left text-sm text-gray-600">
                                <thead className="bg-gray-50 text-gray-800 uppercase font-semibold text-xs border-b border-gray-200">
                                    <tr>
                                        <th className="px-6 py-4">Full Name</th>
                                        <th className="px-6 py-4">Contact Number</th>
                                        <th className="px-6 py-4">Address</th>
                                        <th className="px-6 py-4 text-center">Interested</th>
                                        <th className="px-6 py-4">Submitted At</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {filteredSurveys.length > 0 ? (
                                        filteredSurveys.map((survey) => (
                                            <tr key={survey.id} className="hover:bg-blue-50/50 transition-colors">
                                                <td className="px-6 py-4 font-medium text-gray-900">{survey.full_name}</td>
                                                <td className="px-6 py-4">{survey.contact_number}</td>
                                                <td className="px-6 py-4 max-w-xs truncate" title={survey.address}>{survey.address}</td>
                                                <td className="px-6 py-4 text-center">
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                        survey.interested ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                                    }`}>
                                                        {survey.interested ? 'Yes' : 'No'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-gray-500 text-xs">
                                                    {new Date(survey.submitted_at).toLocaleString(undefined, { 
                                                        year: 'numeric', month: 'short', day: 'numeric', 
                                                        hour: '2-digit', minute: '2-digit' 
                                                    })}
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                                                <svg className="mx-auto h-12 w-12 text-gray-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                                </svg>
                                                <p className="text-lg font-medium">No surveys found</p>
                                                <p className="text-sm">Try adjusting your search or filters.</p>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default Survey;
